<template>
    <g v-if="item" class="map-item" :z-index="item.zIndex" :transform="getTransform" :style="itemBGStyle"
        :class="[`type-${item.type}`, `category-${item.category}`, `size-${item.size || 'md'}`,
            { 'square': isSquare, 'item-selected': selected, 'disable-selection': !item.selectable, 'active': active, 'ghost': ghost }]">
        <g class="fixed-px-size">
            <circle @mousedown.self="$emit('selected', item)" class="selector"
                :class="{ 'hidden': !currentMap.style.iconCircleVisible }" />

            <template v-if="iconVisible">
                <template v-if="!isSimplePoint && isGlyphTheme && itemGlyph">
                    <defs v-if="itemBGStyle">
                        <linearGradient :id="`bg-${item.id}`" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stop-color="lch(from var(--map-icon-bg) calc(l + 5) c h)"/>
                            <stop offset="100%" stop-color="lch(from var(--map-icon-bg) calc(l - 15) c h)"/>
                        </linearGradient>
                    </defs>
                    <rect v-if="isSquare" class="nav-glyph-container" rx="4"/>
                    <circle v-else class="nav-glyph-container"/>
                    <text class="nav-glyph" :class="itemGlyph" v-html="faClassToUnicode(itemGlyph)" :style="itemGlyphStyle"/>
<!--                    <image v-if="renderGlyphAsset" class="glyph rounded fas" :href="glyphHref" />-->
                </template>
                <image v-else class="icon" @mousedown.self="$emit('selected', item)" :href="item.getIcon()" />
            </template>

            <template v-if="labelVisible">
                <e-svg-text-wrap :text="item.getName()" class="map-label name" :class="labelPositionClass"
                                 :bottom-to-top="item.labelPosition === `top`" line-height="1.35" :wrap-length="15">
                    <template v-if="(active || selected) && item.getSubtitle()">
                        <tspan v-if="tr(item.getName())" class="subtitle" x="0" :dy="item.labelPosition === `top` ? `-1.5em` : `1.5em`">
                            {{ item.getSubtitle() }}
                        </tspan>
                        <tspan v-else x="0" dy="0">
                            {{ item.getSubtitle() }}
                        </tspan>
                    </template>

                    <tspan v-if="isMapDebugVisible" x="0" dy="1.35em">[{{ item.index }}]</tspan>
                </e-svg-text-wrap>
            </template>
        </g>
    </g>
</template>

<script>
import MapMixin from "@/helpers/maps/map-mixin.js";
import ESvgTextWrap from "@vue-components/components/svg/e-svg-text-wrap.vue";

// map-item-point
export default {
    name: `map-item-point`,
    components: {ESvgTextWrap},
    mixins: [MapMixin],
    props: {
        item: { type: Object },
        floor: { type: Object },
        layers: { type: Object },
        navEnabled: { type: Boolean },
        active: { type: Boolean },
        selected: { type: Boolean },
        showStats: { type: Boolean },
        rangeDisplay: { type: String, default: `long` },
        apStats: { type: Object },
        iconVisible: { type: Boolean, default: true },
        labelVisible: { type: Boolean, default: false },
        ghost: { type: Boolean, default: true }
    },
    computed: {
        getTransform() {
            return `translate(${this.item.x} ${this.item.y})`;
        },
        isGlyphTheme() {
            return this.currentMap?.style?.icons?.theme === `glyph`;
        },
        labelPosition() {
            if (!this.iconVisible) {
                return `center`;
            }
            return this.item.labelPosition || `auto`;
        },
        labelPositionClass() {
            return `position-${this.labelPosition}`;
        },
        isSquare() {
            return this.item.zone || this.item.category === `navigation` || this.item.category === `services`;
        },
        isSimplePoint() {
            return this.item.type === `nav-point` || this.item.type === `node` || this.item.type === `directions`;
        },
        itemBGStyle() {
            if (!this.isGlyphTheme)
                return null;

            let color = null;
            if (this.item.icon?.bg) {
                color = this.item.icon.bg;
            } else if (this.item.cmsItem &&
                this.currentMap.integrations.cms.colors?.enabled &&
                this.item.cmsItem[this.currentMap.integrations.cms.colors.field] &&
                this.currentMap.integrations.cms.colors.display === `bg`) {
                color = this.item.cmsItem[this.currentMap.integrations.cms.colors.field];
            }

            if (color) {
                return {
                    "--map-icon-bg": `${color}`,
                    "--map-icon-gradient": `url(#bg-${this.item.id})`
                };
            } else {
                return null;
            }
        },
        itemGlyphStyle() {
            if (!this.isGlyphTheme)
                return null;

            let color = null;
            // test with BG to know if color was customized
            if (this.icon?.bg !== null) {
                color = this.item.icon?.color;
            }
            if (!color &&
                this.item.cmsItem &&
                this.currentMap.integrations.cms.colors?.enabled &&
                this.item.cmsItem[this.currentMap.integrations.cms.colors.field] &&
                this.currentMap.integrations.cms.colors.display === `icon`) {
                color = this.item.cmsItem[this.currentMap.integrations.cms.colors.field];
            }

            if (color) {
                return {fill: `${color} !important`};
            } else {
                return null;
            }
        },
        itemGlyph() {
            return this.item.getGlyph(`fas`);
        }
    },
    methods: {
        faClassToUnicode(faClass) {
            // Create a temporary hidden element with the FA class
            const el = document.createElement(`i`);
            el.className = `fa ${faClass}`;           // e.g. "fa fa-hospital"
            el.style.cssText = `position:absolute; visibility:hidden`;
            document.body.appendChild(el);

            // Read the unicode from the ::before content
            const content = getComputedStyle(el, `::before`).content;
            document.body.removeChild(el);
            if (!content || content.trim() === ``) {
                console.error(`missing icon for ${faClass}`);
            }

            // content comes back as a quoted character e.g. '"" '
            // Strip quotes and return the raw character
            return content.replace(/['"/]/g, ``);
        }
    }
}
</script>

<style lang="scss" scoped>
.map-item {
    z-index: 1;
    cursor: pointer;
    --icon-scale: 1;
    --icon-shape-factor: 1;

    --map-icon-gradient: url(#iconBG);
    --icon-size: calc(var(--map-icon-size) * var(--icon-scale) * var(--icon-shape-factor));
    --translate-transform: translate(calc(var(--icon-size) * -0.5), calc(var(--icon-size) * -0.5));

    &.disable-selection,
    .icon {
        pointer-events: none;
    }

    &.square {
        // square would be much bigger than circle otherwise
        --icon-shape-factor: 0.9;
    }

    &.ghost {
        opacity: 0.8 !important;
        pointer-events: none !important;
    }

    .icon {
        pointer-events: none;
        width: var(--icon-size);
        height: var(--icon-size);
        transform: var(--translate-transform);
    }

    rect.nav-glyph-container,
    circle.nav-glyph-container {
        stroke: #fff;
        stroke-width: 1.5px;
        pointer-events: none;
        fill: var(--map-icon-gradient);
        filter: url(#shadow);
    }

    rect.nav-glyph-container {
        width: calc(var(--icon-size));
        height: calc(var(--icon-size));
        transform: var(--translate-transform);
    }

    circle.nav-glyph-container {
        r: calc(var(--icon-size) * 0.5);
    }

    &.size-xs {
        --icon-scale: 0.7;
    }

    &.size-sm {
        --icon-scale: 0.85;
    }

    &.size-lg {
        --icon-scale: 1.1;
    }

    &.size-xl {
        --icon-scale: 1.2;
    }

    &.size-xxl {
        --icon-scale: 1.5;
    }

    circle.selector {
        r: calc(var(--map-icon-size) * 0.85);
        fill: rgba(255, 255, 255, 0.2);
        stroke: rgba(255, 255, 255, 0.1);

        &.hidden {
            fill: transparent;
            stroke: transparent;
        }

        stroke-width: 0.1px;

        &:hover {
            fill: rgba(var(--selection-color), 0.3) !important;
            stroke: rgb(var(--selection-color)) !important;
        }
    }

    &:hover {
        .icon {
            transform: scale(1.2) var(--translate-transform);
        }
    }

    &.active {
        .icon {
            transform: scale(1.5) var(--translate-transform);
        }
    }

    .nav-glyph {
        pointer-events: none;
        fill: white;
        text-anchor: middle;
        dominant-baseline: central;
        font-size: calc(var(--icon-size) / 1.8);
    }
}

.icons-variant-light .map-item {
    .nav-glyph-container {
        stroke: #777;
        stroke-width: 1px;
    }

    .nav-glyph {
        fill: #000;
    }
}
</style>
